import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ROLE_LABELS, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, UserCircle, PencilSimple, Key, PaperPlaneTilt, DotsThreeVertical } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTableSort } from "@/lib/useTableSort";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export default function Users() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwDialog, setPwDialog] = useState(null); // user object oder null

  const load = async () => {
    const { data } = await api.get("/users");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const sendResetLink = async (u) => {
    if (!await window._domivusConfirm(`Reset-Link an ${u.email} senden?`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      toast.success(data?.email_sent ? `Reset-Link an ${u.email} versendet` : `Reset-Link erstellt (kein E-Mail-Versand)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset fehlgeschlagen");
    }
  };

  const { sorted, SortableTh } = useTableSort(items, "name", "asc", {
    role: (u) => ROLE_LABELS[u.role] || u.role || "",
    status: (u) => (u.is_active ? "aktiv" : "inaktiv"),
    created_at: (u) => u.created_at || "",
  });

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Benutzerverwaltung</h1>
          <p className="text-sm text-slate-500 mt-1">Benutzer und Rollen verwalten</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="new-user-btn">
              <Plus size={16} /> Neuer Benutzer
            </Button>
          </DialogTrigger>
          <UserDialog editing={editing} onSaved={() => { setOpen(false); setEditing(null); load(); }} />
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <SortableTh field="name">Name</SortableTh>
              <SortableTh field="email">E-Mail</SortableTh>
              <SortableTh field="role">Rolle</SortableTh>
              <SortableTh field="status">Status</SortableTh>
              <SortableTh field="created_at">Angelegt</SortableTh>
              <Th className="w-24 text-right"></Th>
            </tr>
          </thead>          <tbody>
            {sorted.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Keine Benutzer.</td></tr>}
            {sorted.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <Td className="font-medium text-slate-900">
                  <div className="flex items-center gap-2">
                    <UserAvatar user={u} />
                    <div>
                      <div>{u.name}</div>
                      {u.position && <div className="text-[11px] text-slate-500">{u.position}</div>}
                    </div>
                  </div>
                </Td>
                <Td className="text-slate-700">{u.email}</Td>
                <Td>{ROLE_LABELS[u.role] || u.role}</Td>
                <Td>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                    {u.is_active ? "Aktiv" : "Inaktiv"}
                  </span>
                </Td>
                <Td className="text-xs tabular-nums">{formatDate(u.created_at)}</Td>
                <Td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`edit-user-${u.id}`} title="Bearbeiten">
                      <PencilSimple size={14} />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`user-more-${u.id}`} title="Weitere Aktionen">
                          <DotsThreeVertical size={16} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[220px]">
                        <DropdownMenuItem onClick={() => sendResetLink(u)} data-testid={`user-reset-link-${u.id}`}>
                          <PaperPlaneTilt size={14} className="mr-2" /> Passwort-Reset-Link senden
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPwDialog(u)} data-testid={`user-set-pw-${u.id}`}>
                          <Key size={14} className="mr-2" /> Passwort direkt setzen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pwDialog && (
        <SetPasswordDialog
          user={pwDialog}
          open={!!pwDialog}
          onClose={() => setPwDialog(null)}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}

function UserAvatar({ user }) {
  const [broken, setBroken] = useState(false);
  if (user?.avatar_url && !broken) {
    const src = user.avatar_url.startsWith("http")
      ? user.avatar_url
      : `${process.env.REACT_APP_BACKEND_URL || ""}${user.avatar_url}`;
    return (
      <img
        src={src}
        alt={user.name || "Avatar"}
        className="w-8 h-8 rounded-full object-cover border border-slate-200"
        data-testid={`user-avatar-${user.id}`}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center" data-testid={`user-avatar-${user.id}`}>
      <UserCircle size={20} className="text-slate-500" />
    </div>
  );
}

function UserDialog({ editing, onSaved }) {
  const buildDefault = (e) => e
    ? {
        name: e.name, email: e.email, role: e.role, is_active: e.is_active, password: "",
        first_name: e.first_name || "", last_name: e.last_name || "",
        position: e.position || "", salutation: e.salutation || "", phone: e.phone || "", extension: e.extension || "",
        code: e.code || "",
        signature_block: e.signature_block || "",
        editor_font_family: e.editor_font_family || "",
        editor_font_size: e.editor_font_size || "",
        visible_property_ids: e.visible_property_ids ?? null, // null = unbeschränkt
        allowed_modules: e.allowed_modules ?? null,
      }
    : {
        name: "", email: "", role: "clerk", is_active: true, password: "",
        first_name: "", last_name: "", position: "", salutation: "", phone: "", extension: "",
        code: "",
        signature_block: "",
        editor_font_family: "",
        editor_font_size: "",
        visible_property_ids: null,
        allowed_modules: null,
      };
  const [form, setForm] = useState(() => buildDefault(editing));
  const [properties, setProperties] = useState([]);
  const [tenantModules, setTenantModules] = useState([]);

  useEffect(() => { setForm(buildDefault(editing)); }, [editing]);

  // Properties + Tenant-Module nur laden, wenn Edit-Modus
  useEffect(() => {
    if (!editing) return;
    api.get("/properties").then(({ data }) => setProperties(data || [])).catch(() => {});
    api.get("/tenants/current").then(({ data }) => setTenantModules(data?.modules || [])).catch(() => {});
  }, [editing]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const togglePropertyId = (pid) => {
    const cur = form.visible_property_ids;
    if (cur === null) {
      // von "alle" → "nur dieses" wechseln
      set("visible_property_ids", [pid]);
      return;
    }
    if (cur.includes(pid)) set("visible_property_ids", cur.filter((x) => x !== pid));
    else set("visible_property_ids", [...cur, pid]);
  };

  const toggleModule = (mod) => {
    const cur = form.allowed_modules;
    if (cur === null) {
      set("allowed_modules", [mod]);
      return;
    }
    if (cur.includes(mod)) set("allowed_modules", cur.filter((x) => x !== mod));
    else set("allowed_modules", [...cur, mod]);
  };

  const autoCode = ((form.first_name?.[0] || "") + (form.last_name?.[0] || "")).toUpperCase();

  const submit = async (e) => {
    e.preventDefault();
    try {
      // auto-build name when first/last present
      const computedName = (form.first_name || form.last_name)
        ? `${form.first_name || ""} ${form.last_name || ""}`.trim()
        : form.name;
      if (editing) {
        await api.patch(`/users/${editing.id}`, {
          name: computedName, email: form.email, role: form.role, is_active: form.is_active,
          first_name: form.first_name || null, last_name: form.last_name || null,
          position: form.position || null, salutation: form.salutation || "", phone: form.phone || null, extension: form.extension || null,
          code: form.code || null,
          signature_block: form.signature_block || null,
          editor_font_family: form.editor_font_family || null,
          editor_font_size: form.editor_font_size ? Number(form.editor_font_size) : null,
          visible_property_ids: form.visible_property_ids,
          allowed_modules: form.allowed_modules,
        });
        toast.success("Benutzer aktualisiert");
      } else {
        await api.post("/auth/register", {
          name: computedName || form.name, email: form.email, password: form.password, role: form.role,
          first_name: form.first_name || null, last_name: form.last_name || null,
          position: form.position || null, salutation: form.salutation || "", phone: form.phone || null, extension: form.extension || null,
          code: form.code || null,
          signature_block: form.signature_block || null,
        });
        toast.success("Benutzer angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  // Restricted roles: zeige standardmäßig die erweiterten Restriktionen
  const isRestrictableRole = ["clerk", "manager", "support", "vermietung", "external_service", "viewer"].includes(form.role);

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle>{editing ? "Benutzer bearbeiten" : "Neuer Benutzer"}</DialogTitle>
        <DialogDescription className="text-xs text-slate-500">
          {editing
            ? "Persönliche Stammdaten, Rolle, Status und Position aktualisieren."
            : "Neuen Benutzer für diesen Mandanten anlegen – die hier gepflegte Position erscheint in der Unterschrift von Briefen und Abrechnungen."}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Vorname"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="user-first-name" /></F>
          <F label="Nachname"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="user-last-name" /></F>
        </div>
        <F label="Anzeigename (auto aus Vor-/Nachname)">
          <Input value={(form.first_name || form.last_name) ? `${form.first_name || ""} ${form.last_name || ""}`.trim() : form.name}
                 onChange={(e) => set("name", e.target.value)} data-testid="user-name" />
        </F>
        <F label="E-Mail"><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email" /></F>
        {editing && (
          <div className="text-[11px] text-slate-500 -mt-2">
            Änderung wirkt sich sofort auf Anmeldung und Signaturen aus. Bitte den Benutzer informieren.
          </div>
        )}
        {!editing && (
          <F label="Passwort"><Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password" /></F>
        )}
        <div className="grid grid-cols-2 gap-3">
          <F label="Rolle">
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Anrede">
            <Select value={form.salutation || "none"} onValueChange={(v) => set("salutation", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="user-salutation"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">— keine —</SelectItem><SelectItem value="herr">Herr</SelectItem><SelectItem value="frau">Frau</SelectItem><SelectItem value="divers">Divers (ohne Anrede)</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Position / Funktion (Unterschrift)">
            <Input value={form.position} onChange={(e) => set("position", e.target.value)}
              placeholder="z. B. Objektbetreuung"
              data-testid="user-position" />
          </F>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Telefon"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="user-phone" /></F>
          <F label="Durchwahl"><Input value={form.extension} onChange={(e) => set("extension", e.target.value)} data-testid="user-ext" /></F>
        </div>
        <F label="Mein Zeichen (Kürzel)">
          <Input
            value={form.code}
            onChange={(e) => set("code", e.target.value.toUpperCase())}
            placeholder={autoCode || "z. B. MR"}
            maxLength={6}
            className="font-mono uppercase w-32"
            data-testid="user-code"
          />
          <div className="text-[11px] text-slate-500 mt-1">Wird in Briefen als &bdquo;Mein Zeichen&ldquo; oben rechts ausgewiesen. Leer = Initialen ({autoCode || "—"}).</div>
        </F>

        {/* Phase 21β-4 — Signatur (überschreibt automatische Default-Signatur) */}
        <div className="border-t border-slate-200 pt-3 mt-3 space-y-2">
          <div className="flex items-baseline justify-between">
            <Label className="text-xs text-slate-700">Signatur (optional)</Label>
            <span className="text-[10px] text-slate-400">Leer = automatisch aus Name + Position</span>
          </div>
          <textarea
            value={form.signature_block}
            onChange={(e) => set("signature_block", e.target.value)}
            placeholder={`Mit freundlichen Grüßen\n\n${form.first_name || "Vorname"} ${form.last_name || "Nachname"}\n${form.position || "Position"}`}
            rows={5}
            className="w-full text-sm font-mono leading-snug border border-slate-200 rounded-sm px-3 py-2 focus:outline-none focus:border-slate-400 bg-slate-50"
            data-testid="user-signature-block"
          />
        </div>

        {/* Phase 21β-4 — Editor-Voreinstellungen (nur für neue Dokumente) */}
        <div className="border-t border-slate-200 pt-3 mt-3">
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
            Editor-Voreinstellungen (gilt nur für neue Dokumente)
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Schriftart">
              <Select value={form.editor_font_family || "_default"} onValueChange={(v) => set("editor_font_family", v === "_default" ? "" : v)}>
                <SelectTrigger data-testid="user-font-family"><SelectValue placeholder="Standard (Helvetica)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_default">Standard (Helvetica)</SelectItem>
                  <SelectItem value="Helvetica">Helvetica</SelectItem>
                  <SelectItem value="Arial">Arial</SelectItem>
                  <SelectItem value="Calibri">Calibri</SelectItem>
                  <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                  <SelectItem value="Georgia">Georgia</SelectItem>
                  <SelectItem value="Inter">Inter</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Schriftgröße (pt)">
              <Select value={String(form.editor_font_size || "_default")}
                      onValueChange={(v) => set("editor_font_size", v === "_default" ? "" : v)}>
                <SelectTrigger data-testid="user-font-size"><SelectValue placeholder="Standard (11)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_default">Standard (11)</SelectItem>
                  {[9, 10, 10.5, 11, 11.5, 12, 13, 14].map((s) => (
                    <SelectItem key={s} value={String(s)}>{s} pt</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
          </div>
        </div>
        {editing && (
          <F label="Status">
            <Select value={form.is_active ? "active" : "inactive"} onValueChange={(v) => set("is_active", v === "active")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="inactive">Inaktiv</SelectItem>
              </SelectContent>
            </Select>
          </F>
        )}

        {/* Phase 21z: Objekt-Sichtbarkeit + Modul-Override (nur für eingeschränkte Rollen sichtbar im Edit-Modus) */}
        {editing && isRestrictableRole && (
          <div className="border-t border-slate-200 pt-3 mt-3 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
              Sichtbarkeits-Einschränkungen (Phase 21z)
            </div>

            {/* Objektbezogene Sichtbarkeit */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="user-property-restrictions">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs font-semibold text-slate-800">Objekte sichtbar</div>
                  <div className="text-[11px] text-slate-500">
                    {form.visible_property_ids === null
                      ? "Aktuell: ALLE Objekte sichtbar (Standard)"
                      : `Aktuell: nur ${form.visible_property_ids.length} ausgewählte Objekte`}
                  </div>
                </div>
                <Button
                  type="button" size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => set("visible_property_ids", form.visible_property_ids === null ? [] : null)}
                  data-testid="toggle-property-restriction"
                >
                  {form.visible_property_ids === null ? "Einschränken" : "Alle erlauben"}
                </Button>
              </div>
              {form.visible_property_ids !== null && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-40 overflow-y-auto bg-white border border-slate-200 rounded p-2">
                  {properties.length === 0 && <div className="text-[11px] text-slate-500">Keine Objekte vorhanden.</div>}
                  {properties.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer hover:bg-slate-50 px-1 rounded">
                      <input
                        type="checkbox"
                        checked={form.visible_property_ids.includes(p.id)}
                        onChange={() => togglePropertyId(p.id)}
                        data-testid={`prop-restriction-${p.id}`}
                      />
                      <span className="font-mono text-[10px] text-slate-500">{p.property_number || "—"}</span>
                      <span className="truncate">{p.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Modul-Override */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="user-module-restrictions">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs font-semibold text-slate-800">Module zugänglich</div>
                  <div className="text-[11px] text-slate-500">
                    {form.allowed_modules === null
                      ? "Aktuell: alle Mandanten-Module nutzbar (Standard)"
                      : `Aktuell: nur ${form.allowed_modules.length} Module freigeschaltet`}
                  </div>
                </div>
                <Button
                  type="button" size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => set("allowed_modules", form.allowed_modules === null ? [] : null)}
                  data-testid="toggle-module-restriction"
                >
                  {form.allowed_modules === null ? "Einschränken" : "Alle erlauben"}
                </Button>
              </div>
              {form.allowed_modules !== null && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-32 overflow-y-auto bg-white border border-slate-200 rounded p-2">
                  {tenantModules.length === 0 && <div className="text-[11px] text-slate-500 col-span-3">Keine Module aktiv.</div>}
                  {tenantModules.map((m) => (
                    <label key={m} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.allowed_modules.includes(m)}
                        onChange={() => toggleModule(m)}
                        data-testid={`mod-restriction-${m}`}
                      />
                      <span className="font-mono text-[10px]">{m}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="user-save-btn">
            {editing ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, children }) {
  return <div><Label className="text-xs text-slate-700">{label}</Label><div className="mt-1">{children}</div></div>;
}


function SetPasswordDialog({ user, open, onClose }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwörter stimmen nicht überein"); return; }
    if (pw.length < 6) { toast.error("Mindestens 6 Zeichen"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/set-password`, { new_password: pw });
      toast.success(`Passwort für ${user.name || user.email} gesetzt`);
      setPw(""); setPw2("");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Setzen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Passwort direkt setzen</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Vergibt für <strong>{user.name || user.email}</strong> ein neues Passwort. Bitte anschließend sicher weitergeben.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <F label="Neues Passwort">
            <Input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-set-pw-1" />
          </F>
          <F label="Passwort wiederholen">
            <Input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="admin-set-pw-2" />
          </F>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="admin-set-pw-save">
              {saving ? "Speichere…" : "Passwort setzen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
